<template>
  <div class="copy-payment">
    <el-card class="payment-card">
      <div class="order-header">
        <h2>Copy Order Details</h2>
        <div class="order-status" :class="orderInfo.status.toLowerCase()">
          {{ orderInfo.status }} PAYMENT
        </div>
      </div>

      <div class="order-content">
        <div class="content-info">
          <img 
            v-if="orderInfo.content_info?.cover_url" 
            :src="orderInfo.content_info?.cover_url" 
            class="content-cover"
            alt="Content cover"
          />
          <div class="content-details">
            <span class="content-type" :class="orderInfo.content_type">
              {{ orderInfo.content_type }}
            </span>
            <h3>{{ orderInfo.content_info?.title }}</h3>
            <p class="price">${{ orderInfo.usd_price }} per copy</p>
          </div>
        </div>

        <div class="order-details">
          <div class="detail-item">
            <span class="label">Order ID:</span>
            <span class="value">{{ orderInfo._id?.$id || '-' }}</span>
          </div>

          <div class="detail-item">
            <span class="label">Total Copies:</span>
            <span class="value">{{ orderInfo.quantity }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Total Amount:</span>
            <span class="value">${{ orderInfo.total_price }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Order Date:</span>
            <span class="value">{{ formatDate(orderInfo.order_time*1000) }}</span>
          </div>
        </div>

        <div class="payment-section" v-if="orderInfo.status === 'PENDING'">
          <el-button 
            type="primary" 
            @click="processPayment" 
            :loading="processing"
          >
            Pay Now
          </el-button>
          <el-button 
            type="primary" 
            plain
            @click="downloadInvoice" 
            :loading="downloading"
          >
            Download Payment Invoice
          </el-button>
          <el-button 
            type="success" 
            plain
            @click="sendInvoice" 
            :loading="sending"
          >
            Send Payment Invoice
          </el-button>
          <router-link to="/home/copyOrders" class="view-orders-link">
            View All Orders
          </router-link>
        </div>
      </div>
    </el-card>
    <div class="cancel-order">
      <el-button type="text" @click="cancelOrder" :loading="cancelling" v-if="orderInfo.status === 'PENDING'">
        Cancel this order
      </el-button>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'

export default {
  name: 'CopyPayment',
  data() {
    return {
      orderInfo: {
        status: 'PENDING',
        content: {},
        order_id: '',
        quantity: 0,
        total_price: 0,
        order_time: null
      },
      selectedPayment: 'credit_card',
      processing: false,
      downloading: false,
      sending: false,
      cancelling: false
    }
  },
  created() {
    this.fetchOrderInfo()
  },
  methods: {
    async fetchOrderInfo() {
      try {
        const response = await postRequest('getCopyOrder', {
          order_id: this.$route.query.order_id
        })
        if (response.data.code === 0) {
          this.orderInfo = response.data.data
        }
      } catch (error) {
        console.error('Error fetching order info:', error)
        this.$message.error('Failed to load order information')
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp).toLocaleString()
    },
    async processPayment() {
      this.processing = true
      try {
        // Redirect to payment page with order ID and content type
        this.$router.push({
          path: 'payment',
          query: { 
            order_id: this.$route.query.order_id,
            content_type: this.orderInfo.content_type 
          }
        })
      } catch (error) {
        console.error('Payment error:', error)
        this.$message.error('Failed to process payment')
      } finally {
        this.processing = false
      }
    },
    async downloadInvoice() {
      this.downloading = true
      try {
        const response = await postRequest('./index.php', {
          function: 'downloadCopyInvoice',
          order_id: this.$route.query.order_id
        })
        
        if (response.data.code === 0) {
          window.open(response.data.data.invoice_url, '_blank')
          this.$message.success('Invoice downloaded successfully')
        }
      } catch (error) {
        console.error('Download error:', error)
        this.$message.error('Failed to download invoice')
      } finally {
        this.downloading = false
      }
    },
    async sendInvoice() {
      this.sending = true
      try {
        const response = await postRequest('./index.php', {
          function: 'sendCopyInvoice',
          order_id: this.$route.query.order_id
        })
        
        if (response.data.code === 0) {
          this.$message.success('Invoice sent successfully')
        }
      } catch (error) {
        console.error('Send error:', error)
        this.$message.error('Failed to send invoice')
      } finally {
        this.sending = false
      }
    },
    async cancelOrder() {
      try {
        await this.$confirm(
          'Are you sure you want to cancel this order?',
          'Cancel Order',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning'
          }
        )

        this.cancelling = true
        const response = await postRequest('cancelCopyOrder', {
          order_id: this.$route.query.order_id
        })
        
        if (response.data.code === 0) {
          this.$message.success('Order cancelled successfully')
          await this.fetchOrderInfo()
        }
      } catch (error) {
        if (error === 'cancel') return
        console.error('Cancel error:', error)
        this.$message.error('Failed to cancel order')
      } finally {
        this.cancelling = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.copy-payment {
  padding: 20px;

  .payment-card {
    max-width: 800px;
    margin: 0 auto;

    .order-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;

      h2 {
        margin: 0;
      }

      .order-status {
        padding: 4px 12px;
        border-radius: 4px;
        font-weight: 500;

        &.pending {
          background-color: #fdf6ec;
          color: #e6a23c;
        }

        &.completed {
          background-color: #f0f9eb;
          color: #67c23a;
        }
      }
    }

    .content-info {
      display: flex;
      gap: 20px;
      margin-bottom: 24px;
      padding-bottom: 24px;
      border-bottom: 1px solid #ebeef5;

      .content-cover {
        width: 120px;
        height: 168px;
        object-fit: cover;
        border-radius: 8px;
      }

      .content-details {
        .content-type {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          margin-bottom: 8px;

          &.events {
            background-color: #e1f3d8;
            color: #67c23a;
          }

          &.posts {
            background-color: #fdf6ec;
            color: #e6a23c;
          }
        }

        h3 {
          margin: 0 0 8px;
          color: #303133;
        }

        .price {
          color: #f56c6c;
          font-weight: 500;
          margin: 0;
        }
      }
    }

    .order-details {
      margin-bottom: 24px;

      .detail-item {
        display: flex;
        margin-bottom: 12px;

        .label {
          width: 120px;
          color: #909399;
        }

        .value {
          color: #303133;
          font-weight: 500;
        }
      }
    }

    .payment-section {
      h3 {
        margin-bottom: 16px;
      }

      .payment-methods {
        margin-bottom: 24px;
      }

      .view-orders-link {
        display: block;
        text-align: right;
        color: #409EFF;
        text-decoration: none;
        margin-top: 16px;
        font-size: 14px;
        
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

.cancel-order {
  text-align: center;
  margin-top: 16px;

  .el-button {
    color: #909399;
    font-weight: normal;
  }
}
</style> 